import { el, iconBtn, iconSvg, note, switchRow, textBtn, type DesignerCtx } from './panels.js';
import { openDialog, openPopover } from './overlay.js';
import { SHORTCUTS } from './keyboard.js';
import { ZOOM_MENU, formatZoom, nextZoom } from './zoom.js';
import { applyDocument, designDocument } from './design-file.js';

/**
 * The bottom-left pill: zoom, ⚙ settings, ? help, and Deli.
 *
 * Everything here is about LOOKING at the design rather than changing it,
 * which is why it sits away from the tools and why the settings dialog says so
 * in as many words. The one exception is "Datoteka dizajna" — saving and
 * loading a layout — which the reference editor keeps behind its own menu and
 * which has no business taking a rail slot from Tekst or Slike.
 */

export interface BottomBarHandle {
  readonly element: HTMLElement;
  /** Re-reads the zoom level and the switch states. */
  sync(): void;
  destroy(): void;
}

export function createBottomBar(ctx: DesignerCtx): BottomBarHandle {
  const bar = el('div', 'pzd-bottom');
  bar.setAttribute('role', 'toolbar');
  bar.setAttribute('aria-label', ctx.texts.zoomLevel);

  const out = iconBtn('minus', ctx.texts.zoomOut, () => ctx.setView({ zoom: nextZoom(ctx.view.zoom, -1) }), {
    className: 'pzd-bbtn',
    size: 16,
  });
  const level = el('button', 'pzd-bbtn is-level');
  level.type = 'button';
  level.setAttribute('aria-label', ctx.texts.zoomLevel);
  const levelText = el('span', undefined, formatZoom(ctx.view.zoom));
  const levelChevron = el('span', 'pzd-tchev');
  levelChevron.innerHTML = iconSvg('chevron', 12);
  level.append(levelText, levelChevron);
  level.addEventListener('click', () => openZoomMenu(ctx, level));
  const into = iconBtn('plus', ctx.texts.zoomIn, () => ctx.setView({ zoom: nextZoom(ctx.view.zoom, 1) }), {
    className: 'pzd-bbtn',
    size: 16,
  });

  const settings = iconBtn('settings', ctx.texts.settings, () => openSettingsDialog(ctx), { className: 'pzd-bbtn' });
  const help = iconBtn('help', ctx.texts.help, () => openHelpPopover(ctx, help), { className: 'pzd-bbtn' });

  bar.append(out, level, into, el('span', 'pzd-bsep'), settings, help);
  // Zazzle's print-location button sits in the same pill: "Front / Front Pocket".
  if (ctx.printLocations && ctx.switchPrintLocation) {
    const location = iconBtn('location', ctx.texts.printLocation, () => openLocationMenu(ctx, location), { className: 'pzd-bbtn' });
    bar.append(location);
  }
  if (ctx.share || ctx.library) {
    bar.append(iconBtn('share', ctx.texts.shareDesign, () => openShareDialog(ctx), { className: 'pzd-bbtn' }));
  }

  return {
    element: bar,
    sync() {
      levelText.textContent = formatZoom(ctx.view.zoom);
    },
    destroy() {
      bar.remove();
    },
  };
}

function openZoomMenu(ctx: DesignerCtx, anchor: HTMLElement): void {
  openPopover({
    ctx,
    anchor,
    above: true,
    className: 'is-menu',
    title: ctx.texts.zoomLevel,
    build: (body, handle) => {
      for (const zoom of ZOOM_MENU) {
        const item = el('button', 'pzd-menu-item');
        item.type = 'button';
        const label = zoom === 1 ? `${formatZoom(zoom)} (${ctx.texts.zoomFit})` : formatZoom(zoom);
        item.textContent = label;
        item.setAttribute('aria-pressed', String(Math.abs(ctx.view.zoom - zoom) < 0.001));
        if (Math.abs(ctx.view.zoom - zoom) < 0.001) item.classList.add('is-on');
        item.addEventListener('click', () => {
          ctx.setView({ zoom, ...(zoom === 1 ? { panMode: false } : {}) });
          // One pick is the whole errand; leaving the menu up invites a second.
          handle.close();
        });
        body.append(item);
      }
    },
  });
}

function openLocationMenu(ctx: DesignerCtx, anchor: HTMLElement): void {
  openPopover({
    ctx,
    anchor,
    above: true,
    className: 'is-menu',
    title: ctx.texts.printLocation,
    build: (body, handle) => {
      for (const location of ctx.printLocations ?? []) {
        const item = el('button', `pzd-menu-item${location.current ? ' is-on' : ''}`);
        item.type = 'button';
        item.textContent = location.name;
        item.setAttribute('aria-pressed', String(location.current));
        item.addEventListener('click', () => {
          handle.close();
          ctx.switchPrintLocation?.(location.id);
        });
        body.append(item);
      }
      body.append(note(ctx.texts.printLocationNote));
    },
  });
}

/** The eight view switches, in the reference's order, plus the design file. */
export function openSettingsDialog(ctx: DesignerCtx): void {
  const rows: Array<[keyof typeof ctx.view, string, string]> = [
    ['miniToolbar', ctx.texts.setMiniToolbar, ctx.texts.setMiniToolbarNote],
    ['darkMode', ctx.texts.setDarkMode, ctx.texts.setDarkModeNote],
    ['lockAspect', ctx.texts.setLockAspect, ctx.texts.setLockAspectNote],
    ['snap', ctx.texts.setSnap, ctx.texts.setSnapNote],
    ['guidelines', ctx.texts.setGuidelines, ctx.texts.setGuidelinesNote],
    ['rulers', ctx.texts.setRulers, ctx.texts.setRulersNote],
    ['grid', ctx.texts.setGrid, ctx.texts.setGridNote],
    ['transparency', ctx.texts.setTransparency, ctx.texts.setTransparencyNote],
  ];
  openDialog({
    ctx,
    title: ctx.texts.settings,
    className: 'is-settings',
    build: (body) => {
      for (const [key, label, explanation] of rows) {
        const row = el('div', 'pzd-setting');
        row.append(switchRow(label, Boolean(ctx.view[key]), (on) => {
          ctx.setView({ [key]: on } as Record<string, boolean>);
        }));
        row.append(el('span', 'pzd-hint', explanation));
        body.append(row);
      }
      body.append(note(ctx.texts.viewNote));
      body.append(el('h5', 'pzd-over-sub', ctx.texts.fileSection));
      body.append(fileSection(ctx));
    },
  });
}

function fileSection(ctx: DesignerCtx): HTMLElement {
  const box = el('div', 'pzd-file-section');
  box.append(note(ctx.texts.fileNote));
  box.append(textBtn(ctx.texts.fileDownload, () => {
    const blob = new Blob([JSON.stringify(designDocument(ctx), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = el('a');
    anchor.href = url;
    anchor.download = 'personalizy-design.json';
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, { icon: 'upload', className: 'pzd-command' }));

  const mode = el('select');
  mode.setAttribute('aria-label', ctx.texts.fileLoad);
  for (const [value, label] of [['replace', ctx.texts.fileReplace], ['append', ctx.texts.fileAppend]] as const) {
    const option = el('option', undefined, label);
    option.value = value;
    mode.append(option);
  }
  const scale = el('select');
  scale.setAttribute('aria-label', ctx.texts.fileLoad);
  for (const [value, label] of [['fit', ctx.texts.fileFitScale], ['physical', ctx.texts.filePhysical]] as const) {
    const option = el('option', undefined, label);
    option.value = value;
    scale.append(option);
  }
  const file = el('input');
  file.type = 'file';
  file.accept = '.json,application/json';
  file.setAttribute('aria-label', ctx.texts.fileLoad);
  file.addEventListener('change', () => {
    const picked = file.files?.[0];
    if (!picked) return;
    if (picked.size > 128000) {
      ctx.toast(ctx.texts.fileTooBig, 'error');
      return;
    }
    void picked.text()
      .then((text) => applyDocument(
        ctx,
        JSON.parse(text),
        mode.value as 'replace' | 'append',
        scale.value as 'fit' | 'physical',
      ))
      .catch(() => ctx.toast(ctx.texts.fileBroken, 'error'));
  });
  box.append(mode, scale, file);
  box.append(
    textBtn(ctx.texts.fileCopyLayers, () => ctx.copySelection(), { className: 'pzd-command' }),
    textBtn(ctx.texts.filePasteLayers, () => ctx.pasteSelection(), { className: 'pzd-command' }),
  );
  return box;
}

/** The guideline legend, and the way into the shortcuts table. */
export function openHelpPopover(ctx: DesignerCtx, anchor: HTMLElement): void {
  openPopover({
    ctx,
    anchor,
    above: true,
    title: ctx.texts.help,
    className: 'is-help',
    build: (body) => {
      const legend: Array<[string, string, string]> = [
        ['bleed', ctx.texts.helpBleed, ctx.texts.helpBleedNote],
        ['visible', ctx.texts.helpVisible, ctx.texts.helpVisibleNote],
        ['safe', ctx.texts.helpSafe, ctx.texts.helpSafeNote],
      ];
      for (const [kind, title, explanation] of legend) {
        const row = el('div', 'pzd-legend-row');
        row.append(el('span', `pzd-legend-line is-${kind}`));
        const copy = el('div');
        copy.append(el('strong', undefined, title), el('span', 'pzd-hint', explanation));
        row.append(copy);
        body.append(row);
      }
      body.append(textBtn(ctx.texts.shortcutsButton, () => openShortcutsDialog(ctx), { className: 'pzd-command' }));
    },
  });
}

/** Exactly the table `keyboard.ts` implements — no row without a handler. */
export function openShortcutsDialog(ctx: DesignerCtx): void {
  openDialog({
    ctx,
    title: ctx.texts.shortcutsTitle,
    className: 'is-shortcuts',
    build: (body) => {
      const list = el('dl', 'pzd-shortcuts');
      for (const row of SHORTCUTS) {
        list.append(el('dt', undefined, String(ctx.texts[row.label])));
        list.append(el('dd', undefined, row.keys));
      }
      body.append(list);
    },
  });
}

/** "Moji dizajni": save this design under a name, and the list of saved ones. */
function librarySection(ctx: DesignerCtx, body: HTMLElement): void {
  const library = ctx.library;
  if (!library) return;
  const section = el('div', 'pzd-library');
  section.append(el('h4', 'pzd-library-title', ctx.texts.libraryTitle));
  const row = el('div', 'pzd-library-save');
  const name = el('input', 'pzd-input');
  name.type = 'text';
  name.maxLength = 80;
  name.value = library.suggestName?.() ?? '';
  name.setAttribute('aria-label', ctx.texts.libraryName);
  name.placeholder = ctx.texts.libraryName;
  const status = el('div', 'pzd-hint');
  status.setAttribute('role', 'status');
  const list = el('div', 'pzd-library-list');
  const paint = (): void => {
    list.textContent = '';
    const entries = library.list();
    if (!entries.length) {
      list.append(note(ctx.texts.libraryEmpty));
      return;
    }
    for (const entry of entries) {
      const item = el('div', 'pzd-library-item');
      const thumb = el('span', 'pzd-library-thumb');
      if (entry.thumb) {
        const img = el('img');
        img.src = entry.thumb;
        img.alt = '';
        thumb.append(img);
      }
      const text = el('span', 'pzd-library-text');
      const when = new Date(entry.at).toLocaleDateString('sr-RS');
      text.append(el('b', undefined, entry.name), el('small', undefined, [entry.productName, when].filter(Boolean).join(' · ')));
      const open = el('a', 'pzd-command', ctx.texts.libraryOpen);
      open.href = entry.url;
      const remove = iconBtn('trash', ctx.texts.libraryRemove, () => {
        library.remove(entry.id);
        paint();
      }, { className: 'pzd-bbtn' });
      item.append(thumb, text, open, remove);
      list.append(item);
    }
  };
  const save = textBtn(ctx.texts.librarySave, () => {
    save.disabled = true;
    status.textContent = ctx.texts.shareWorking;
    void library.save(ctx.store.value(), name.value.trim() || (library.suggestName?.() ?? ''))
      .then(() => {
        status.textContent = ctx.texts.librarySaved;
        paint();
      })
      .catch((error: unknown) => {
        status.textContent = error instanceof Error ? error.message : ctx.texts.shareFailed;
      })
      .finally(() => {
        save.disabled = false;
      });
  }, { className: 'pzd-primary' });
  row.append(name, save);
  section.append(row, status, note(ctx.texts.libraryNote), list);
  body.append(section);
  paint();
}

export function openShareDialog(ctx: DesignerCtx): void {
  openDialog({
    ctx,
    title: ctx.texts.shareTitle,
    className: 'is-share',
    build: (body) => {
      librarySection(ctx, body);
      if (!ctx.share) return;
      const output = el('div');
      const make = textBtn(ctx.texts.shareMake, () => {
        make.disabled = true;
        output.textContent = ctx.texts.shareWorking;
        void ctx.share?.()
          .then((link) => {
            output.textContent = '';
            const input = el('input', 'pzd-input');
            input.readOnly = true;
            input.value = link;
            input.setAttribute('aria-label', ctx.texts.shareTitle);
            input.addEventListener('focus', () => input.select());
            output.append(input, textBtn(ctx.texts.shareCopy, () => {
              void navigator.clipboard?.writeText(link)
                .then(() => ctx.toast(ctx.texts.shareCopied, 'success'))
                .catch(() => input.select());
            }, { className: 'pzd-command' }), note(ctx.texts.shareNote));
          })
          .catch((error: unknown) => {
            output.textContent = error instanceof Error ? error.message : ctx.texts.shareFailed;
          })
          .finally(() => {
            make.disabled = false;
          });
      }, { className: 'pzd-command' });
      body.append(make, output);
    },
  });
}
